import {
  type OverrideConflictContent,
  OverrideDialog,
  getOverrideConflictContent,
} from "@/components/OverrideDialog";
import {
  Avatar,
  AvatarFallback,
  BodyText,
  Button,
  TableCell,
  TableRow,
  Tooltip,
  toast,
} from "@/components/ui";
import {
  CMS_ACCESS_ENROLLMENT_FLAG,
  programTrackDisplayName,
} from "@/constants/AccessProgram";
import { TRACKERS } from "@/constants/Trackers";
import { useOpenPatientChartPreview } from "@/hooks/useOpenPatientChart";
import { usePartner } from "@/hooks/usePartner";
import { usePatientTimer } from "@/hooks/usePatientTimer";
import { trackEvent } from "@/lib/tracking";
import { cn, getInitials } from "@/lib/utils";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import {
  TAKE_ENCOUNTER_ERROR_TOAST_ID,
  getTakeEncounterErrorMessage,
  useTakeEncounter,
} from "@/network/useTakeEncounter";
import { useUser } from "@/network/useUser";
import { selectFallbackPartnerUid } from "@/utils/partnerLogo";
import { useRouter } from "@tanstack/react-router";
import { motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { formatPatientAgeGender } from "../Patient/PatientBar/utils/formatPatientAgeGender";
import type {
  WaitingRoomEncounter,
  WaitingRoomItem,
} from "./network/useWaitingRoomDashboard";

interface LiveQueueRowProps {
  message: WaitingRoomItem;
  /** Enter/exit + color animations, gated by the live-queue-freeze flag. */
  animate?: boolean;
  /** Whether a freshly-mounted row should flash green (suppressed on first paint). */
  flashOnMount?: boolean;
}

// Animated table row: opacity enter fade + red-tinted exit fade run through
// Motion (reorders intentionally don't animate — layout transforms on <tr> are
// janky). Rows are non-interactive while exiting so a fading-out row can't be
// taken by mistake. All motion is disabled under prefers-reduced-motion.
const MotionTableRow = motion.create(TableRow);

export const LiveQueueRow = ({
  message,
  animate = true,
  flashOnMount = false,
}: LiveQueueRowProps) => {
  const { navigate } = useRouter();
  const reduceMotion = useReducedMotion();
  const shouldAnimate = animate && !reduceMotion;

  // Briefly tint newly-inserted rows green, then let the color transition fade
  // it back out. Done via a class (not Motion) so no inline background lingers
  // to override the row's hover highlight.
  const [justAdded, setJustAdded] = useState(shouldAnimate && flashOnMount);
  useEffect(() => {
    if (!justAdded) return;
    const timer = setTimeout(() => setJustAdded(false), 600);
    return () => clearTimeout(timer);
  }, [justAdded]);
  const [overrideContent, setOverrideContent] =
    useState<OverrideConflictContent | null>(null);
  const { data: user } = useUser();
  const openPatientChartPreview = useOpenPatientChartPreview();
  const { patient, encounter } = message;

  const { isPending, mutateAsync } = useTakeEncounter({
    patientId: patient.id,
    encounterId: encounter.id,
    showErrorToast: false,
  });

  const { customerWaitingTimer } = usePatientTimer(
    encounter.customerWaitingSince,
    encounter.slaBreachTime,
    {
      patientId: patient.id,
      encounterId: encounter.id,
    },
  );
  const { partner } = usePartner(
    patient.preferredPartnerUid ??
      selectFallbackPartnerUid(patient.partnerUids),
  );
  const { age, administrativeGender } = patient;

  const patientState = encounter.tempAddressState ?? patient.addressState;
  const isLicensedInState = (user?.professional?.licenses ?? []).some(
    (license) =>
      license.state === patientState &&
      new Date(license.expiresAt) > new Date(),
  );
  const canOverrideLicense = message.metadata.canOverrideLicense;
  const isMissingPedsQualification =
    patient.isPeds && !user?.professional?.isQualifiedForPediatrics;

  const navigateToPatient = useCallback(() => {
    navigate({
      to: "/patient/$patientId/timeline",
      params: {
        patientId: patient.id,
      },
      search: {
        encounterId: encounter.id,
      },
    });
  }, [navigate, patient.id, encounter.id]);

  const handleClickTake = useCallback(
    async (e: MouseEvent, overrideBlock = false) => {
      trackEvent(TRACKERS.TAKE_PATIENT);
      e.stopPropagation();
      try {
        await mutateAsync(overrideBlock);
        setOverrideContent(null);
        navigateToPatient();
      } catch (error: unknown) {
        // Show the override dialog for overridable blocks; otherwise toast the
        // error. The two are mutually exclusive so the toast never doubles up
        // with the dialog.
        const content = getOverrideConflictContent(error);
        if (content) {
          setOverrideContent(content);
        } else {
          toast.error(getTakeEncounterErrorMessage(error), {
            id: TAKE_ENCOUNTER_ERROR_TOAST_ID,
          });
        }
      }
    },
    [mutateAsync, navigateToPatient],
  );

  const takeButton = useMemo(() => {
    const button = (
      <Button
        variant={"primary"}
        data-testid="live-queue-take"
        size="sm"
        className="leading-tight"
        onClick={(e) => handleClickTake(e)}
        isLoading={isPending}
        disabled={
          (!isLicensedInState && !canOverrideLicense) ||
          isMissingPedsQualification
        }
      >
        Take
      </Button>
    );
    if (
      (!isLicensedInState && !canOverrideLicense) ||
      isMissingPedsQualification
    ) {
      const tooltipContent =
        !isLicensedInState && !canOverrideLicense
          ? "You are not licensed in the patient's state."
          : "You are not qualified for pediatrics.";
      return (
        <Tooltip side="bottom" tooltipContentText={tooltipContent}>
          {button}
        </Tooltip>
      );
    }
    return button;
  }, [
    isLicensedInState,
    canOverrideLicense,
    isMissingPedsQualification,
    isPending,
    handleClickTake,
  ]);

  return (
    <>
      <MotionTableRow
        initial={shouldAnimate ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        exit={
          shouldAnimate
            ? {
                // Flash red while still fully opaque, THEN fade out — otherwise
                // the opacity drop masks the tint before it's visible.
                backgroundColor: [
                  "rgba(239, 68, 68, 0)",
                  "rgba(239, 68, 68, 0.4)",
                  "rgba(239, 68, 68, 0.4)",
                ],
                opacity: [1, 1, 0],
                pointerEvents: "none",
                transition: {
                  duration: 0.8,
                  ease: "easeInOut",
                  times: [0, 0.4, 1],
                },
              }
            : undefined
        }
        transition={{ duration: shouldAnimate ? 0.5 : 0, ease: "easeInOut" }}
        className={cn(
          "w-12 hover:bg-secondary focus-visible:bg-secondary cursor-pointer whitespace-nowrap transition-colors",
          // Green flash on insert; the color transition fades it out.
          justAdded && shouldAnimate && "bg-green-100 duration-700",
        )}
        onClick={() => openPatientChartPreview({ patient, encounter })}
        data-testid={`live-queue-row-${patient.id}`}
      >
        <TableCell className="max-w-64">
          <Tooltip
            side="bottom"
            tooltipContentText={`${patient.nameFirst} ${patient.nameLast}`}
          >
            <div className="flex items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap">
              <Avatar className="w-4 h-4">
                <AvatarFallback className="bg-yellow-400">
                  <BodyText className="text-[8px]">
                    {getInitials(`${patient.nameFirst} ${patient.nameLast}`)}
                  </BodyText>
                </AvatarFallback>
              </Avatar>
              <BodyText
                size="sm"
                className="leading-tight overflow-hidden text-ellipsis whitespace-nowrap"
              >
                {patient.nameFirst} {patient.nameLast}
              </BodyText>
            </div>
          </Tooltip>
        </TableCell>
        <TableCell>
          <BodyText
            size="sm"
            className={cn("rounded-md p-1", patient.isPeds && "bg-info")}
          >
            {formatPatientAgeGender({ age, administrativeGender })}
            {patient.isPeds && (
              <BodyText
                size="xs"
                textColor="white"
                className="bg-button-info rounded-full ml-1 w-3.5 h-3.5 text-center inline-block content-center"
              >
                P
              </BodyText>
            )}
          </BodyText>
        </TableCell>
        <TableCell>
          <BodyText size="sm">
            {encounter.tempAddressState || patient.addressState}
          </BodyText>
        </TableCell>
        <TableCell>
          <img src={partner?.logoUrl} alt={partner?.name} className="w-4 h-4" />
        </TableCell>
        <TableCell className="max-w-64">
          <RFECell encounter={encounter} />
        </TableCell>
        <TableCell>
          <div className="float-right flex items-center gap-3">
            <BodyText size="sm" tag="span" textColor="tertiary">
              {message.encounter.suggestedPractitionerName}
            </BodyText>
            <BodyText size="sm" textColor={"primary"}>
              {customerWaitingTimer}
            </BodyText>
            {takeButton}
          </div>
        </TableCell>
      </MotionTableRow>
      <OverrideDialog
        isOpen={overrideContent !== null}
        onOpenChange={(open) => {
          if (!open) {
            setOverrideContent(null);
          }
        }}
        onOverride={() => {
          const syntheticEvent = { stopPropagation: () => {} } as MouseEvent;
          handleClickTake(syntheticEvent, true);
        }}
        isLoading={isPending}
        title={overrideContent?.title ?? ""}
        messages={overrideContent?.messages}
      />
    </>
  );
};

const getRFEPrefix = (encounter: WaitingRoomEncounter) => {
  const prefixes: string[] = [];

  if (encounter.isRxRequest) prefixes.push("Rx Request");
  if (encounter.isRxRenewalRequest) prefixes.push("Rx Renewal");
  if (encounter.isAsyncDxRequest) prefixes.push("Async Dx");
  if (encounter.isAv) prefixes.push("AV Establish Care");
  if (encounter.isDfr) prefixes.push("DFR");
  if (encounter.isFollowUp) prefixes.push("Follow Up");
  if (encounter.isHandOff) prefixes.push("Handoff");
  if (encounter.isInProgress) prefixes.push("In Progress");

  return prefixes.join(" | ");
};

const RFECell = ({ encounter }: { encounter: WaitingRoomEncounter }) => {
  const { data: accessFlag } = useConfigCatFeatureFlag(
    CMS_ACCESS_ENROLLMENT_FLAG,
  );
  const accessEnabled = accessFlag === true;

  if (accessEnabled && encounter.programType) {
    const typeLabel = programTrackDisplayName(encounter.programType);
    const tooltipText = `AV ACCESS ${typeLabel}`;
    return (
      <Tooltip side="bottom" tooltipContentText={tooltipText}>
        <div className="overflow-hidden text-ellipsis whitespace-nowrap flex items-center gap-1">
          <BodyText size="sm" tag="span" textColor="tertiary">
            AV ACCESS
          </BodyText>
          <BodyText size="sm" tag="span">
            {typeLabel}
          </BodyText>
        </div>
      </Tooltip>
    );
  }

  const prefix = getRFEPrefix(encounter);
  const rfe = encounter.rfe || "n/a";

  const tooltipText = `${prefix} | ${rfe}`;

  const renderRFE = () =>
    encounter.rfe ? (
      encounter.rfe
    ) : (
      <BodyText size="sm" tag="span" textColor="tertiary">
        n/a
      </BodyText>
    );

  return (
    <Tooltip side="bottom" tooltipContentText={tooltipText}>
      <div className="overflow-hidden text-ellipsis whitespace-nowrap flex items-center gap-1">
        <>
          {prefix && (
            <BodyText size="sm" tag="span" textColor="tertiary">
              {prefix}
            </BodyText>
          )}
          <BodyText size="sm" tag="span">
            {renderRFE()}
          </BodyText>
        </>
      </div>
    </Tooltip>
  );
};
